import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface Course {
  id: number
  name: string
}

interface Owner {
  id: number
  user_id: number
  first_name: string
  last_name: string
  username: string
  berechtigung: 'owner' | 'editor' | 'viewer'
}

interface OwnersModalProps {
  type: string
  item: Course
  onClose: () => void
}

export default function OwnersModal({ type, item, onClose }: OwnersModalProps) {
  const [owners, setOwners] = useState<Owner[]>([])
  const [newOwnerId, setNewOwnerId] = useState<number | null>(null)
  const [newPermission, setNewPermission] = useState<'owner' | 'editor' | 'viewer'>('viewer')
  const [users, setUsers] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    fetchOwners()
    fetchUsers()
  }, [item])

  const fetchOwners = async () => {
    try {
      const endpoint = `/courses/${item.id}/owners`
      const response = await api.get(endpoint)
      console.log('Fetch owners response:', response.data) // Debug log
      setOwners(response.data)
    } catch (error) {
      console.error('Error fetching owners:', error)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/simple')
      console.log('Fetched users:', response.data) // Debug log
      setUsers(response.data.filter((user: any) => user.role !== 'student'))
    } catch (error) {
      console.error('Error fetching users:', error)
      // Fallback to regular users endpoint if simple fails
      try {
        const fallbackResponse = await api.get('/users')
        setUsers(fallbackResponse.data.filter((user: any) => user.role !== 'student'))
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError)
      }
    }
  }

  const handleAddOwner = async () => {
    if (!newOwnerId) return
    
    setIsLoading(true)
    try {
      const endpoint = `/courses/${item.id}/owners`
      const response = await api.post(endpoint, {
        userId: newOwnerId,
        berechtigung: newPermission
      })
      console.log('Add owner response:', response) // Debug log
      await fetchOwners()
      setNewOwnerId(null)
      setNewPermission('viewer')
      toast.success('Eigentümer erfolgreich hinzugefügt!')
    } catch (error: any) {
      console.error('Add owner error:', error) // Debug log
      toast.error(error.response?.data?.message || 'Fehler beim Hinzufügen des Eigentümers')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveOwner = async (userId: number) => {
    if (!window.confirm('Möchten Sie diesen Eigentümer wirklich entfernen?')) return
    
    setIsLoading(true)
    try {
      const endpoint = `/courses/${item.id}/owners/${userId}`
      await api.delete(endpoint)
      await fetchOwners()
      toast.success('Eigentümer erfolgreich entfernt!')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Fehler beim Entfernen des Eigentümers')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdatePermission = async (userId: number, newPermission: string) => {
    setIsLoading(true)
    try {
      const endpoint = `/courses/${item.id}/owners/${userId}`
      await api.put(endpoint, { berechtigung: newPermission })
      await fetchOwners()
      toast.success('Berechtigung erfolgreich aktualisiert!')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Fehler beim Aktualisieren der Berechtigung')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Eigentümer verwalten - {item.name}
          </h3>
          
          {/* Current Owners */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Aktuelle Eigentümer</h4>
            <div className="space-y-2">
              {owners.map((owner) => (
                <div key={owner.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                      <span className="text-sm font-medium text-gray-700">
                        {owner.first_name?.charAt(0) || '?'}{owner.last_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {owner.first_name || 'Unbekannt'} {owner.last_name || 'Benutzer'}
                      </p>
                      <p className="text-xs text-gray-500">@{owner.username}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={owner.berechtigung}
                      onChange={(e) => handleUpdatePermission(owner.user_id, e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                      disabled={isLoading}
                    >
                      <option value="owner">Eigentümer</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Betrachter</option>
                    </select>
                    <button
                      onClick={() => handleRemoveOwner(owner.user_id)}
                      className="text-red-400 hover:text-red-600"
                      disabled={isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add New Owner */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Neuen Eigentümer hinzufügen</h4>
            <div className="flex space-x-3">
              <select
                value={newOwnerId || ''}
                onChange={(e) => setNewOwnerId(parseInt(e.target.value))}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Benutzer auswählen...</option>
                {users
                  .filter(user => !owners.some(owner => owner.user_id === user.id))
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name || 'Unbekannt'} {user.last_name || 'Benutzer'} (@{user.username})
                    </option>
                  ))}
              </select>
              <select
                value={newPermission}
                onChange={(e) => setNewPermission(e.target.value as 'owner' | 'editor' | 'viewer')}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="viewer">Betrachter</option>
                <option value="editor">Editor</option>
                <option value="owner">Eigentümer</option>
              </select>
              <button
                onClick={handleAddOwner}
                disabled={!newOwnerId || isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Hinzufügen...' : 'Hinzufügen'}
              </button>
            </div>
          </div>

          {/* Permission Legend */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h5 className="text-sm font-medium text-blue-900 mb-2">Berechtigungen:</h5>
            <div className="text-xs text-blue-700 space-y-1">
              <p><strong>Eigentümer:</strong> Vollzugriff (erstellen, bearbeiten, löschen, Eigentümer verwalten)</p>
              <p><strong>Editor:</strong> Bearbeiten (bearbeiten, Teilnehmer verwalten, aber nicht löschen)</p>
              <p><strong>Betrachter:</strong> Nur anzeigen (lesen, aber keine Änderungen)</p>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
